import statusBarData from "@/static/features/statusBarData";
import { pickFirstProductLevelStatusString } from "@/utils/activeProjectProductUtils";
import { getActiveAssessment } from "@/utils/productInfoDetailsSelectors";
import { normalizeActiveProjectStatusForStepLookup, normalizeStatusCharactersToSlugKey } from "@/utils/statusBarStatusAliases";

export function resolvePackCopyStatusForStep(
  vendor: Record<string, unknown> | null | undefined,
  projectBusinessStatus: string | null | undefined
): { step: number | null; usedProjectLevelStatus: boolean } {
  const v = vendor as { vendorAssessmentStatus?: string; businessStatus?: string } | undefined;
  const va = v?.vendorAssessmentStatus;
  if (va != null && String(va).trim() !== "") {
    return { step: getStepNumberFromStatus(String(va)), usedProjectLevelStatus: false };
  }
  const vb = v?.businessStatus;
  if (vb != null && String(vb).trim() !== "") {
    return { step: getStepNumberFromStatus(String(vb)), usedProjectLevelStatus: false };
  }
  return {
    step: getStepNumberFromStatus(projectBusinessStatus),
    usedProjectLevelStatus: true,
  };
}

export function getStepNumberFromStatus(status: string | null | undefined): number | null {
  if (!status || typeof status !== "string") return null;
  const cleaned = String(status)
    .replace(/\u00a0/g, " ")
    .replace(/\s*\([^)]*\)\s*/g, " ")
    .trim();
  if (!cleaned) return null;
  /** Canonical key: same pipeline as `ReusableStatusBar` (aliases → statusBarData keys/labels). */
  const lookupKey = normalizeActiveProjectStatusForStepLookup(cleaned);
  const steps = statusBarData?.STATUS_BAR?.statusSteps;
  if (!steps) return null;
  const labelMap = statusBarData?.STATUS_BAR?.statusLabelMap ?? {};
  for (const [key, config] of Object.entries(steps)) {
    const stepConfig = config as { stepNo?: number };
    const keyNorm = normalizeStatusCharactersToSlugKey(key);
    const label = (labelMap as Record<string, string>)[key];
    const labelNorm = label ? normalizeStatusCharactersToSlugKey(label) : "";
    if (keyNorm === lookupKey || labelNorm === lookupKey) {
      return stepConfig?.stepNo ?? null;
    }
    const statusMap = (config as { statusMap?: string[] }).statusMap;
    if (Array.isArray(statusMap)) {
      for (const m of statusMap) {
        const mRaw = String(m ?? "").trim();
        if (!mRaw) continue;
        if (normalizeActiveProjectStatusForStepLookup(mRaw) === lookupKey) {
          return stepConfig?.stepNo ?? null;
        }
      }
    }
  }
  return null;
}

/** Workflow markers used for Pack Copy tab visibility (flattened vendorList rows). */
export function workflowIncludesPackCopyTabEligible(workflow: string[] | null | undefined): boolean {
  if (!Array.isArray(workflow)) return false;
  return workflow.some((s) => {
    const n = String(s ?? "")
      .trim()
      .toLowerCase();
    return n.includes("generated_nt_pack_copy");
  });
}

export function workflowContainsActiveProjectsPhaseMarkers(workflow: string[] | null | undefined): boolean {
  if (!Array.isArray(workflow)) return false;
  return workflow.some((raw) => {
    const s = String(raw ?? "")
      .trim()
      .toLowerCase();
    if (!s) return false;
    if (s.includes("ps_")) return true;
    if (s.includes("generated_nt_pack_copy")) return true;
    if (s.includes("vo_ps")) return true;
    if (s.includes("compliance_document_uploaded") || s.includes("product_specification_uploaded")) return true;
    return false;
  });
}

export function resolveProductLevelStatusForPackCopyTab(
  project: Record<string, unknown> | null | undefined
): string {
  if (!project || typeof project !== "object") return "";
  const raw = pickFirstProductLevelStatusString(project);
  if (!raw) return "";
  return raw
    .replace(/\u00a0/g, " ")
    .replace(/\s*\([^)]*\)\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** `FINAL_SPECIFICATION_REVIEW_AND_ACKNOWLEDGMENT_VENDOR.stepNo` from `statusBarData` (Artwork File tab threshold). */
export function getFinalSpecificationReviewAcknowledgementVendorStepNumberFromConfig(): number | null {
  const steps = statusBarData?.STATUS_BAR?.statusSteps;
  return (steps?.FINAL_SPECIFICATION_REVIEW_AND_ACKNOWLEDGEMENT_VENDOR as { stepNo?: number } | undefined)?.stepNo ?? null;
}

/** Step 10 = Under Internal Review; Pack Copy / Artwork File tabs require `vendorAssessmentStatus` step ≥ 10 (no product-level override). */
export const PACK_COPY_TAB_VISIBILITY_MIN_STEP = 10;

/** True when `vendorAssessmentStatus` maps to status-bar step ≥ 10 (Under Internal Review onwards). */
function vendorAssessmentStatusMeetsPackCopyTabMinimum(
  vendorOrMerged: Record<string, unknown>,
  minStep: number
): boolean {
  const vaRaw = vendorOrMerged.vendorAssessmentStatus;
  if (vaRaw == null || String(vaRaw).trim() === "") return false;
  const step = getStepNumberFromStatus(String(vaRaw));
  if (step == null) return false;
  return step >= minStep;
}

/**
 * Active Projects ARTWORK_FILE tab: requires `vendorAssessmentStatus` ≥ step 10 (Under Internal Review onward),
 * then resolved step ≥ final-spec review acknowledgment (vendor) from config.
 * Does not use product-level status when assessment status is below that floor.
 */
export function vendorMeetsArtworkFileTabVisibilityCriteria(
  vendor: Record<string, unknown> | null | undefined,
  projectBusinessStatus?: string | null
): boolean {
  if (!vendor || typeof vendor !== "object") return false;
  if (!vendorAssessmentStatusMeetsPackCopyTabMinimum(vendor, PACK_COPY_TAB_VISIBILITY_MIN_STEP)) {
    return false;
  }
  const { step } = resolvePackCopyStatusForStep(vendor, projectBusinessStatus ?? null);
  const minStep = getFinalSpecificationReviewAcknowledgementVendorStepNumberFromConfig();
  if (step == null || minStep == null) return false;
  return step >= minStep;
}

/** Flatten vendorDetails[group].vendorList[version] via `getActiveAssessment`; only rows with a non-empty `assessmentId`. */
function someMergedVendorAssessmentRow(
  vendorDetails: unknown[] | null | undefined,
  predicate: (merged: Record<string, unknown>) => boolean
): boolean {
  const vds = Array.isArray(vendorDetails) ? vendorDetails : [];
  const product = { vendorDetails: vds } as Record<string, unknown>;
  for (let g = 0; g < vds.length; g += 1) {
    const group = vds[g];
    if (group == null || typeof group !== "object") continue;
    const go = group as Record<string, unknown>;
    const list = Array.isArray(go.vendorList) ? go.vendorList : [];
    const vendorId = String(go.vendorId ?? go.id ?? "").trim();
    if (list.length === 0 || !vendorId) continue;
    for (let v = 0; v < list.length; v += 1) {
      const merged = getActiveAssessment(product, g, v) as Record<string, unknown> | null;
      if (!merged) continue;
      const aidRaw = merged.assessmentId;
      const aid = aidRaw != null && String(aidRaw).trim() !== "" ? String(aidRaw).trim() : "";
      if (!aid) continue;
      if (predicate(merged)) return true;
    }
  }
  return false;
}

/**
 * Whether this merged assessment row meets the Pack Copy tab step gate (status-bar step ≥ 10 from
 * resolved step + content-mode gate. Used for the Active Projects tab strip for the **selected** vendor/version
 * (`getActiveAssessment` at `activeVendorTabIndex` / `activeVendorVersionIndex`) and for per-row URL alignment.
 */
export function vendorMeetsPackCopyTabVisibilityCriteria(
  vendor: Record<string, unknown> | null | undefined,
  projectBusinessStatus?: string | null
): boolean {
  if (!vendor || typeof vendor !== "object") return false;
  const { step } = resolvePackCopyStatusForStep(vendor, projectBusinessStatus ?? null);
  if (step == null) return false;
  if (step < PACK_COPY_TAB_VISIBILITY_MIN_STEP) return false;

  const wfRaw = (vendor as any)?.workflowStatuses ?? (vendor as any)?.workflowStatus;
  const wf = Array.isArray(wfRaw) ? wfRaw.map((s) => String(s)) : null;
  const mode = getPackCopyContentMode(step, wf);
  
  if (step === PACK_COPY_TAB_VISIBILITY_MIN_STEP) {
    return mode === "full";
  }
  return true;
}

/**
 * True when **any** assessment row (all vendor groups × versions) meets the Pack Copy step gate. Retained for
 * redirect helpers that reason about the whole product; the main tab strip uses `vendorMeetsPackCopyTabVisibilityCriteria`
 * on the **selected** vendor/version only.
 */
export function projectHasAnyVendorEligibleForPackCopyTab(
  vendorDetails: unknown[] | null | undefined,
  _projectBusinessStatus?: string | null
): boolean {
  return someMergedVendorAssessmentRow(vendorDetails, (merged) =>
    vendorAssessmentStatusMeetsPackCopyTabMinimum(merged, PACK_COPY_TAB_VISIBILITY_MIN_STEP)
  );
}

export type PackCopyContentMode = "full" | "restricted" | "redirect";

export function getPackCopyContentMode(
  step: number | null,
  workflow: string[] | null | undefined
): PackCopyContentMode {
  if (step == null) return "full";
  if (step < 6) return "redirect";
  if (step > 10) return "full";
  if (step === 6) return "restricted";
  // steps 7–10 (workflow marker gating removed)
  void workflow;
  return "full";
}
